import { useEffect, useRef } from 'react';

import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

/**
 * Ends the `approval-flow.create` RUM operation that "New flow" on the
 * Approval Flows page (`PageHeader`, `EmptyPage`) starts before navigating
 * to the creation page.
 *
 * - `succeed` as soon as the flow is created.
 * - `fail` when the flow could not be created. A later submit on the same
 *   page starts a new operation, so a retry is measured on its own.
 * - Leaving the page while the operation is open, through "Back" or the
 *   unsaved-changes dialog, fails it as `abandoned`.
 *
 * Opening the page without "New flow" (a direct link) has no start event;
 * Datadog drops the end events of such operations.
 */
export default function useFlowCreationOperation() {
  const isOpenRef = useRef(true);

  useEffect(() => {
    isOpenRef.current = true;

    return () => {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        failOperation('approval-flow.create', 'abandoned');
      }
    };
  }, []);

  const restartIfEnded = () => {
    if (!isOpenRef.current) {
      isOpenRef.current = true;
      startOperation('approval-flow.create', { description: 'retry' });
    }
  };

  const succeed = (context: {
    flowType: string;
    approverGroupCount: number;
  }) => {
    isOpenRef.current = false;
    succeedOperation('approval-flow.create', { context });
  };

  const fail = () => {
    isOpenRef.current = false;
    failOperation('approval-flow.create', 'error');
  };

  return { restartIfEnded, succeed, fail };
}
